import { Autocomplete, Modal, Stack, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import React, { useEffect } from "react";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { postAPICall } from "lib/callApi";
import { USER_CONFIGS, DATE_FILTER_OPTIONS } from "lib/constants";
import { useSelector, useDispatch } from "react-redux";
import { loginUserAction } from "redux/slice/users";
import Loader from "utils/Loader";
import MDDatePicker from "components/MDDatePicker";
import { displayDate } from "utils/Date";
import moment from "moment";
import FilterAutocomplete from "./FilterAutocomplete";

const createModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: window.innerWidth < 600 ? "100%" : 600,
  // height: window.innerHeight < 800 ? 600 : 650,
  overflowX: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "0.5rem",
  p: 4,
  "& .MuiFormHelperText-root": { color: "#d32f2f" },
};

function FilterModal({
  open,
  handleClose,
  filters,
  changeURLBasedOnFilter,
  options,
  setModalFilters,
  syncOptionsAndFilter,
  isModalLoading,
  columns,
}) {
  const dispatch = useDispatch();
  const loginUserState = useSelector((state) => state.loginUser.loginUserState);

  const handleApplyClick = () => {
    if (!isModalLoading) {
      syncOptionsAndFilter(filters, false, true);
    }
  };

  const handleDeleteClick = (key, filter) => {
    if (!isModalLoading) {
      const newFilters = { ...filters, [key]: filter };
      setModalFilters(newFilters);
      syncOptionsAndFilter(newFilters, false, true);
    }
  };

  useEffect(() => {
    if (open) {
      syncOptionsAndFilter(filters, false, true);
    }
  }, [open]);

  const saveAsDefaultFilter = async () => {
    try {
      const concatFiltersAndColumns = {
        ...(filters || {}),
        ...(columns || {}),
      };

      const response = await postAPICall(
        `${USER_CONFIGS}/${loginUserState.userCompanyLinkId}`,
        true,
        {
          value: concatFiltersAndColumns,
          name: "DEFAULT_LEADERBOARD_FILTER",
        }
      );
      if (response?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "info",
            title: "Saved",
            text: response?.data?.message || response.message || "Performance filter saved!",
          }),
          {
            variant: "custom",
          }
        );
        dispatch(
          loginUserAction.getLoginUserData({
            ...loginUserState,
            defaultLeaderboardFilter: JSON.stringify(concatFiltersAndColumns),
          })
        );
        handleClose();
        changeURLBasedOnFilter({ ...filters, pageSize: 10, pageNumber: 0 });
      } else if (response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || response.message || "Could not save data!",
          }),
          {
            variant: "custom",
          }
        );
      } else {
        handleClose();
      }
    } catch (error) {
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || error.message || "Could not update data!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={createModalStyle}>
        <MDBox sx={{ position: "relative", pointeEvents: isModalLoading && "none" }}>
          <Loader loading={isModalLoading} zIndex="10000" />
          <MDTypography
            sx={{ marginBottom: "1rem" }}
            id="modal-modal-title"
            variant="h5"
            component="h2"
          >
            Filters
          </MDTypography>
          <MDBox>
            <MDButton
              color="secondaryBlue"
              onClick={() => {
                syncOptionsAndFilter(
                  {
                    location: [],
                    team: [],
                    user: [],
                    role: [],
                    reportsTo: [],
                    dateFilter: "Today",
                    startDate: "",
                    endDate: "",
                  },
                  false,
                  true
                );
              }}
              sx={{ mb: 2 }}
            >
              Reset Filters
            </MDButton>
          </MDBox>
          <MDBox mb={2} sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <FilterAutocomplete
              field="location"
              options={options.location || []}
              label="Location"
              value={filters?.location}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(e, filter) => {
                setModalFilters((prev) => ({ ...prev, location: filter }));
              }}
            />

            <FilterAutocomplete
              options={options.team || []}
              label="Team"
              field="team"
              value={filters.team || []}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(e, filter) => {
                setModalFilters((prev) => ({ ...prev, team: filter }));
              }}
            />
            <FilterAutocomplete
              label="Role"
              field="role"
              value={filters.role || []}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(e, filter) => {
                setModalFilters((prev) => ({ ...prev, role: filter }));
              }}
              options={options?.role || []}
            />
            <FilterAutocomplete
              label="User"
              field="user"
              value={filters?.user || []}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(e, filter) => {
                setModalFilters((prev) => ({ ...prev, user: filter }));
              }}
              options={options?.user || []}
            />
            <FilterAutocomplete
              label="Reports To"
              field="reportsTo"
              value={filters?.reportsTo?.length ? filters.reportsTo : []}
              onApplyClick={handleApplyClick}
              onDeleteClick={handleDeleteClick}
              onChange={(e, filter) => {
                setModalFilters((prev) => ({ ...prev, reportsTo: filter }));
              }}
              options={options?.reportsTo?.length ? options?.reportsTo : []}
            />
            <Autocomplete
              disablePortal
              disableClearable
              id="combo-box-demo"
              value={filters.dateFilter}
              onChange={(e, filter) => {
                setModalFilters((prev) => ({
                  ...prev,
                  dateFilter: filter,
                  startDate: "",
                  endDate: "",
                }));
              }}
              options={DATE_FILTER_OPTIONS}
              renderInput={(params) => <TextField {...params} label="Filter options" />}
            />
            {filters.dateFilter === "Custom" && (
              <Stack display="flex" flexDirection="row" gap="8px">
                <MDDatePicker
                  input={{
                    placeholder: "Select start date",
                    label: "Start date",
                    value: filters.startDate && displayDate(filters.startDate),
                  }}
                  value={filters.startDate}
                  options={{
                    formatDate: (date) => date && displayDate(date, "MM-DD-YYYY"),
                  }}
                  onChange={(value) => {
                    setModalFilters((prev) => ({
                      ...prev,
                      startDate: moment(value[0]).format("YYYY-MM-DD HH:mm:ss"),
                      endDate: "",
                    }));
                  }}
                />
                <MDDatePicker
                  input={{
                    placeholder: "Select end date",
                    label: "End date",
                    value: filters.endDate && displayDate(filters.endDate),
                  }}
                  value={filters.endDate}
                  options={{
                    formatDate: (date) => date && displayDate(date, "MM-DD-YYYY"),
                  }}
                  onChange={(value) => {
                    setModalFilters((prev) => ({
                      ...prev,
                      endDate: moment(value[0]).format("YYYY-MM-DD HH:mm:ss"),
                    }));
                  }}
                />
              </Stack>
            )}
          </MDBox>
          <MDBox sx={{ display: "flex", justifyContent: "space-between" }}>
            <MDBox>
              <MDButton
                disabled={_.isEqual(
                  filters,
                  JSON.parse(loginUserState?.defaultLeaderboardFilter || "{}")
                )}
                variant="outlined"
                color="gray80"
                sx={{ mt: 2, mr: 1, position: "relative" }}
                onClick={saveAsDefaultFilter}
              >
                Save as default view
              </MDButton>
            </MDBox>
            <MDBox>
              <MDButton
                color="gray80"
                onClick={handleClose}
                sx={{ mt: 2, mr: 1, position: "relative" }}
              >
                Close
              </MDButton>
              <MDButton
                color="primaryRed"
                onClick={() => {
                  changeURLBasedOnFilter({ ...filters, pageSize: 10, pageNumber: 0 });
                  handleClose();
                }}
                sx={{ mt: 2, mr: 1, position: "relative" }}
              >
                Apply
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default FilterModal;
